import Tooltip from "@material-ui/core/Tooltip";
import Button, { ButtonTypeMap } from "@material-ui/core/Button";
import { Paper, Theme } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Draggable from "react-draggable";
import { Resizable } from "re-resizable";

const PANEL_RATIO = 0.72;

const useStyles = makeStyles((theme: Theme) => ({
  btnRoot: {
    textTransform: "none",
  },
  dragHandle: {
    cursor: "move",
    background: theme.palette.grey[200],
    padding: "6px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    userSelect: "none",
    flexShrink: 0,
  },
  graphContent: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    boxSizing: "border-box" as const,
  },
}));

type PanelState = { x: number; y: number; width: number; height: number };

type TreeReferenceButtonProps = {
  title: string;
  tooltipTitle: string;
  disabled?: boolean;
  graphView: any;
  buttonVariant?: ButtonTypeMap["props"]["variant"];
  icon?: any;
};

const TreeReferenceButton = (props: TreeReferenceButtonProps) => {
  const { tooltipTitle, title, graphView, disabled, icon } = props;
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelState>({ x: 0, y: 0, width: 800, height: 500 });
  const resizeStartPanel = useRef<PanelState>(panel);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const classes = useStyles();

  const calcInitialPanel = (): PanelState => {
    const main = buttonRef.current && buttonRef.current.closest("main");
    const area = main
      ? main.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    const w = Math.round(area.width * PANEL_RATIO);
    const h = Math.round(area.height * PANEL_RATIO);
    return {
      x: Math.round(area.left + (area.width - w) / 2),
      y: Math.round(area.top + (area.height - h) / 2),
      width: w,
      height: h,
    };
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <React.Fragment>
      <Tooltip title={tooltipTitle}>
        <Button
          ref={buttonRef}
          className={classes.btnRoot}
          size="small"
          startIcon={icon ? icon : <VisibilityIcon />}
          color="primary"
          variant={"contained"}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setPanel(calcInitialPanel());
            setOpen(true);
          }}
        >
          {title}
        </Button>
      </Tooltip>

      {open && (
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1300 }}>
          <Draggable
            handle="#tree-ref-drag-handle"
            position={{ x: panel.x, y: panel.y }}
            onDrag={(_, data) => setPanel(p => ({ ...p, x: data.x, y: data.y }))}
          >
            <Paper
              elevation={24}
              style={{ position: "absolute", top: 0, left: 0, pointerEvents: "auto", overflow: "hidden", margin: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div id="tree-ref-drag-handle" className={classes.dragHandle}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{title}</span>
                <button
                  onClick={handleClose}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }}
                >
                  ✕
                </button>
              </div>
              <Resizable
                size={{ width: panel.width, height: panel.height }}
                minWidth={300}
                minHeight={200}
                style={{ display: "flex", flexDirection: "column" }}
                onResizeStart={() => { resizeStartPanel.current = { ...panel }; }}
                onResize={(_, __, ___, delta) => {
                  setPanel({
                    x: resizeStartPanel.current.x - delta.width / 2,
                    y: resizeStartPanel.current.y - delta.height / 2,
                    width: resizeStartPanel.current.width + delta.width,
                    height: resizeStartPanel.current.height + delta.height,
                  });
                }}
                onResizeStop={(_, __, ref) => {
                  setPanel(p => ({ ...p, width: ref.offsetWidth, height: ref.offsetHeight }));
                }}
              >
                <div className={classes.graphContent}>{graphView}</div>
              </Resizable>
            </Paper>
          </Draggable>
        </div>
      )}
    </React.Fragment>
  );
};

export default TreeReferenceButton;
